/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { MdOutlineCloudDownload } from "react-icons/md";
import { Button, Input } from "rizzui";
import { IoMdClose } from "react-icons/io";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import toast from "react-hot-toast";
import { DatePicker } from "@/components/ui/datepicker";
import useProfile from "@/hooks/use-profile";
import { Roles } from "@/utilities/types/Users";
import UsersFilter from "@/components/filters/users-filter";
import { useState } from "react";
import { useModal } from "@/app/shared/modal-views/use-modal";
import z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PiTrashDuotone } from "react-icons/pi";

type FormValues = {
  startDate: Date;
  endDate: Date;
  emailAgent: string;
};

type IPropsDownloadAllLeadsModal = {
  closeModal: () => void;
};

const formSchema = z
  .object({
    startDate: z.date().optional(),
    endDate: z.date().optional(),
  })
  .superRefine((data, ctx) => {
    const { startDate, endDate } = data;

    if (startDate && !endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select an end date",
        path: ["endDate"],
      });
    }

    if (!startDate && endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a start date",
        path: ["startDate"],
      });
    }

    if (startDate && endDate && startDate > endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date must be before or equal to end date",
        path: ["endDate"],
      });
    }
  });

export function DownloadAllLeadsModal(props: IPropsDownloadAllLeadsModal) {
  const { closeModal } = props;
  const {
    handleSubmit,
    control,
    register,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const [emailAgent, setEmailAgent] = useState<string>("");
  const [userId, setUserId] = useState<string>();
  const { profile } = useProfile();
  const isAdmin = profile?.role === Roles.ADMIN;

  const onChangeFilterAgency = (email: string, userId?: string) => {
    setEmailAgent(email);
    setUserId(userId);
  };

  const onClear = () => {
    reset();
    setEmailAgent("");
    setUserId(undefined);
  };

  const isClearDisabled =
    !emailAgent && !watch("startDate") && !watch("endDate");

  const onSubmit = async (data: FormValues) => {
    const formattedStart = data.startDate
      ? dayjs(data.startDate).format("DD-MM-YYYY")
      : "";
    const formattedEnd = data.endDate
      ? dayjs(data.endDate).format("DD-MM-YYYY")
      : "";

    const endDate = data.endDate
      ? dayjs(data.endDate).endOf("day").toISOString()
      : undefined;
    const startDate = data.startDate
      ? dayjs(data.startDate).startOf("day").toISOString()
      : undefined;

    const dateDownloaded =
      formattedStart && formattedEnd
        ? `-${formattedStart}-to-${formattedEnd}`
        : "";

    const emailAgentDownloaded = emailAgent ? `-${emailAgent}` : "";

    const autoFileName = `all-leads${emailAgentDownloaded}${dateDownloaded}.csv`;

    try {
      const response = await EnquiryEntriesRepository.getAllLeadsCSVfile({
        ...data,
        startDate,
        endDate,
        userId,
        fileName: autoFileName,
      });
      const blob = new Blob([response], { type: "text/csv;charset=UTF-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", autoFileName);
      link.click();
      link.remove();
      toast.success("File downloaded: " + `${autoFileName}`);
    } catch (e) {
      toast.error("Download failed");
      console.error(e);
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-2 w-full p-6"
    >
      <div className="flex flex-row justify-between items-center mb-6">
        <h5>Download All Leads</h5>
        <IoMdClose size={24} onClick={closeModal} />
      </div>

      <div className="flex flex-row gap-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={field.onChange}
                dateFormat="dd MMM, yyyy"
                placeholderText="Select start date"
                popperPlacement="bottom-end"
                inputProps={{
                  variant: "text",
                  inputClassName: "p-0 px-1 h-auto [&_input]:text-ellipsis",
                }}
                className="w-fit rounded border [&_.rizzui-input-container]:px-3 [&_.rizzui-input-container]:py-1.5"
              />
            )}
          />
          {errors.startDate && (
            <p className="text-sm text-red-500">{errors.startDate.message}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">
            End Date
          </label>
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <DatePicker
                selected={field.value}
                onChange={field.onChange}
                dateFormat="dd MMM, yyyy"
                placeholderText="Select end date"
                popperPlacement="bottom-end"
                inputProps={{
                  variant: "text",
                  inputClassName: "p-0 px-1 h-auto [&_input]:text-ellipsis",
                }}
                className="w-fit rounded border [&_.rizzui-input-container]:px-3 [&_.rizzui-input-container]:py-1.5"
              />
            )}
          />
          {errors.endDate && (
            <p className="text-sm text-red-500">{errors.endDate.message}</p>
          )}
        </div>
      </div>
      {isAdmin && (
        <UsersFilter
          onChange={(user) => {
            onChangeFilterAgency(user?.email || "", user?.id);
          }}
          label="Filter by agent"
          value={emailAgent}
          className="lg:min-w-[250px] w-full"
          placeholder="Select agent"
        />
      )}
      <div className="w-full flex flex-row justify-between mt-4">
        <Button type="button" onClick={onClear} disabled={isClearDisabled}>
          <PiTrashDuotone size={20} className="mr-2" /> Clear
        </Button>
        <Button type="submit">
          <MdOutlineCloudDownload size={20} className="mr-2" />
          Download
        </Button>
      </div>
    </form>
  );
}

export default function DownloadAllLeads() {
  const { openModal, closeModal } = useModal();
  const onClick = () => {
    openModal({
      view: <DownloadAllLeadsModal closeModal={closeModal} />,
    });
  };

  return (
    <Button
      type="button"
      className="min-w-fit max-h-fit p-2 md:p-4"
      onClick={onClick}
    >
      <MdOutlineCloudDownload
        size={24}
        title="Download All Leads"
        className=" hidden md:block md:mr-3"
      />
      <span className="text-xs md:text-base">Download CSV</span>
    </Button>
  );
}
